import { Component, OnInit, ChangeDetectorRef } from "@angular/core";
import { fuseAnimations } from "@fuse/animations";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { DeviceModel } from "app/models/device.model";
import { DeviceService } from "app/services/api/device.service";
import { locale as catalan } from "../../i18n/ca";
import { locale as english } from "../../i18n/en";
import { locale as spanish } from "../../i18n/es";
import { Observable, forkJoin } from "rxjs";
import { ManufacturerModel } from "app/models/manufacturer.model";
import { ManufacturerService } from "app/services/api/manufacturer.service";
import { Cloudinary } from "@cloudinary/url-gen";
import { CloudinaryImage } from "@cloudinary/url-gen/assets/CloudinaryImage";
import { fill, limitFit } from "@cloudinary/url-gen/actions/resize";

@Component({
    selector: "app-devices-admin",
    templateUrl: "./devicesAdmin.component.html",
    styleUrls: ["./devicesAdmin.component.scss"],
    animations: fuseAnimations,
    standalone: false,
})
export class DevicesAdminComponent implements OnInit {
    private readonly cloudinary = new Cloudinary({ cloud: { cloudName: "emmadb" } });
    private readonly cloudinaryImageCache = new Map<string, CloudinaryImage>();

    devices: DeviceModel[] = [];
    filteredDevices: DeviceModel[] = [];
    searchText: string = "";
    isLoading: boolean;
    displayedColumns = [
        "checkbox",
        "icon",
        "picture",
        "name",
        "manufacturer",
        "model",
        "serial-number",
        "device-type",
        "active-project",
        "buttons",
    ];
    checkboxes: any;
    selectedDevices: number;
    manufacturerMap: { [id: string]: ManufacturerModel } = {};

    constructor(
        private devicesService: DeviceService,
        private manufacturerService: ManufacturerService,
        private translation: FuseTranslationLoaderService,
        private cdr: ChangeDetectorRef,
    ) {
        this.translation.loadTranslations(english, spanish, catalan);
        this.isLoading = false;
        this.selectedDevices = 0;
    }

    ngOnInit() {
        this.loadData();
    }

    loadData() {
        this.selectedDevices = 0;
        this.isLoading = true;

        const loadingObservables: Observable<any>[] = [];
        loadingObservables.push(this.devicesService.getAll());
        loadingObservables.push(this.manufacturerService.getAll());

        forkJoin(loadingObservables).subscribe(
            ([devices, manufacturers]) => {
                this.checkboxes = {};

                devices.map((device) => {
                    this.checkboxes[device.id] = false;
                });
                this.devices = devices;
                this.filterDevices();

                manufacturers.map((manufacturer) => {
                    this.manufacturerMap[manufacturer.id] = manufacturer;
                });

                this.isLoading = false;
                this.cdr.detectChanges();
            },
            (error) => {
                this.isLoading = false;
                console.log(error);
                this.cdr.detectChanges();
            },
        );
    }

    onSearchChange(searchText: string) {
        this.searchText = searchText;
        this.filterDevices();
    }

    clearSearch(): void {
        this.onSearchChange("");
    }

    filterDevices() {
        if (!this.searchText) {
            this.filteredDevices = this.devices;
            return;
        }

        const searchLower = this.searchText.toLowerCase();
        this.filteredDevices = this.devices.filter((device) => device.name.toLowerCase().includes(searchLower));
    }

    onSelectedChange(_userID): void {
        this.selectedDevices = 0;
        for (const check in this.checkboxes) {
            if (this.checkboxes[check]) {
                this.selectedDevices++;
            }
        }
    }

    deleteDevice(device: DeviceModel) {
        this.devicesService.delete(device.id).subscribe(
            () => {
                this.loadData();
            },
            (error) => {
                console.log(error);
            },
        );
    }

    deleteDevices() {
        const observables: Observable<any>[] = [];
        for (const check in this.checkboxes) {
            if (this.checkboxes[check]) {
                observables.push(this.devicesService.delete(check));
            }
        }

        forkJoin(observables).subscribe(
            () => {
                this.loadData();
            },
            (error) => {
                console.log(error);
            },
        );
    }

    public getCloudinaryImage(
        publicId: string | null | undefined,
        width: number | null = null,
        height: number | null = null,
        crop: "fill" | "limit" = "limit",
    ): CloudinaryImage | null {
        const id = (publicId || "").trim();
        if (!id || id.startsWith("http://") || id.startsWith("https://") || id.startsWith("data:")) {
            return null;
        }

        const key = `${id}|${width ?? ""}|${height ?? ""}|${crop}`;
        const cached = this.cloudinaryImageCache.get(key);
        if (cached) {
            return cached;
        }

        const image = this.cloudinary.image(id).format("auto").quality("auto");

        if (width !== null || height !== null) {
            const resizeAction = crop === "fill" ? fill() : limitFit();
            if (width !== null) {
                resizeAction.width(width);
            }
            if (height !== null) {
                resizeAction.height(height);
            }
            image.resize(resizeAction);
        }

        this.cloudinaryImageCache.set(key, image);
        return image;
    }
}
